import { Box, Chip, Stack, Typography } from '@mui/material'
import FlightIcon from '@mui/icons-material/Flight'
import AirlinesIcon from '@mui/icons-material/Airlines'
import { useTranslations } from 'next-intl'

import * as COLORS from '@/app/theme/colors/shared'
import HotelSearchForm from '@/src/components/common/home/hotelSearch/searchForm'

interface DestinationHeroProps {
    heading: string
    subheading: string | null
    cityName: string
    countryName: string
    topOrigins: { code: string; name: string }[]
    topAirlines: { code: string; name: string }[]
    weeklyArrivals: number
    isMobile: boolean
}

const DestinationHero: React.FC<DestinationHeroProps> = ({
    heading,
    subheading,
    cityName,
    countryName,
    topOrigins,
    topAirlines,
    weeklyArrivals,
    isMobile
}) => {
    const t = useTranslations('destinationPage')
    return (
        <Box
            component="header"
            sx={{
                bgcolor: COLORS.DESTINATION_HERO_BG,
                position: 'relative',
                overflow: 'hidden',
                pt: { xs: 5, md: 7 },
                pb: { xs: 4, md: 6 }
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: COLORS.DESTINATION_HERO_OVERLAY,
                    zIndex: 0
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    maxWidth: 'lg',
                    mx: 'auto',
                    px: { xs: 2, md: 4 }
                }}
            >
                {(cityName || countryName) && (
                    <Typography
                        variant="overline"
                        sx={{ color: COLORS.DESTINATION_HERO_TEXT_MUTED, letterSpacing: 2, display: 'block', mb: 0.5 }}
                    >
                        {[cityName, countryName].filter(Boolean).join(', ')}
                    </Typography>
                )}

                <Typography
                    component="h1"
                    sx={{
                        color: 'common.white',
                        fontWeight: 700,
                        fontSize: { xs: '1.6rem', md: '2.2rem' },
                        lineHeight: 1.2,
                        mb: subheading ? 1 : 2
                    }}
                >
                    {heading}
                </Typography>

                {subheading && (
                    <Typography
                        variant="body1"
                        sx={{ color: COLORS.DESTINATION_HERO_TEXT_SECONDARY, mb: 3, maxWidth: 600, lineHeight: 1.6 }}
                    >
                        {subheading}
                    </Typography>
                )}

                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 3,
                        boxShadow: COLORS.DESTINATION_HERO_SEARCH_SHADOW
                    }}
                >
                    <HotelSearchForm isMobile={isMobile} />
                </Box>

                {(topOrigins.length > 0 || topAirlines.length > 0 || weeklyArrivals > 0) && (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{ mt: 1 }}
                    >
                        {weeklyArrivals > 0 && (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <FlightIcon sx={{ color: COLORS.DESTINATION_HERO_TEXT_MUTED, fontSize: 16 }} />
                                <Typography variant="caption" sx={{ color: COLORS.DESTINATION_HERO_TEXT_SECONDARY }}>
                                    {weeklyArrivals} {t('weeklyArrivals')}
                                </Typography>
                            </Stack>
                        )}
                        {topAirlines.length > 0 && (
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                <AirlinesIcon sx={{ color: COLORS.DESTINATION_HERO_TEXT_MUTED, fontSize: 16 }} />
                                {topAirlines.slice(0, 3).map(a => (
                                    <Chip
                                        key={a.code}
                                        label={a.name}
                                        size="small"
                                        sx={{
                                            bgcolor: COLORS.DESTINATION_HERO_CHIP_BG,
                                            color: COLORS.DESTINATION_HERO_TEXT_PRIMARY,
                                            fontSize: '0.7rem',
                                            height: 22,
                                            border: `1px solid ${COLORS.DESTINATION_HERO_CHIP_BORDER}`
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
        </Box>
    )
}

export default DestinationHero
export type { DestinationHeroProps }
